import { Children } from 'react';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';

import moment from 'moment-timezone';

import { get } from 'utils/lodash';

import {
  E_WALLET,
  INTERBANK,
  SKN_TRANSFER,
  RTGS_TRANSFER,
  SWIFT_TRANSFER,
  VIRTUAL_ACCOUNT,
  TRANSFER_RECURRING_MONTHLY,
  CONFIRMATION_DETAIL_DATE_FORMAT,
  INTRABANK,
  OWN_ACCOUNT,
} from 'settings/constants/transaction';
import { TODAY } from 'settings/constants/calendar';
import { TAC_EXCEED_MAX_TRIES, SWIFT_EXCEED_CUTOFF_TIME } from 'settings/constants/response-codes';

export const getDebitDate = date => {
  const day = moment(date).day();
  const dayDate = moment(date).format('D');
  const dayName = { id: `calendar.longDays.${day}` };
  return { dayName, dayDate };
};

// below functions can be re-used for other transfer types
export const getConfirmationDetail = (detail, transferType) => {
  const errorCode = get(detail, 'errorCode', '');
  const responseMessage = get(detail, 'responseMessage', '');
  const isSwiftCutOffError = transferType === SWIFT_TRANSFER && errorCode === SWIFT_EXCEED_CUTOFF_TIME;
  // swift transfer display a different message for cutoff time error
  const confirmError = isSwiftCutOffError ? `${errorCode}_${SWIFT_TRANSFER}` : errorCode;

  return {
    errorDetails: { confirmError, responseMessage },
    isSwiftCutOffError,
    isConfirmSuccess: get(detail, 'isSuccess', false),
    tacBean: get(detail, 'tacBean', ''),
  };
};

export const getResultDetail = (detail, transferType) => {
  const errorCode = get(detail, 'errorCode', '');
  const isFavAccount = get(detail, 'isFavAccount', false);
  const isSwiftCutOffError = transferType === SWIFT_TRANSFER && errorCode === SWIFT_EXCEED_CUTOFF_TIME;
  // swift transfer display a different message for cutoff time error
  const resultError = isSwiftCutOffError ? `${errorCode}_${SWIFT_TRANSFER}` : errorCode;
  // used in add favourite
  const addFavObject = get(detail, 'addFavObject', {});
  // swift for transaction over USD 100k, will show a modal
  const showLimitMessage = get(detail, 'showLimitMessage', false);

  return {
    resultError,
    addFavObject,
    isFavAccount,
    showLimitMessage,
    isSwiftCutOffError,
    isTacLocked: resultError === TAC_EXCEED_MAX_TRIES,
    responseMessage: get(detail, 'responseMessage', ''),
    statusCode: get(detail, 'statusCode', ''),
    statusMessage: get(detail, 'statusMessage', ''),
    transactionId: get(detail, 'transactionID', ''),
    isResultSuccess: get(detail, 'isSuccess', false),
    isTransactionSuccess: get(detail, 'isTransactionSuccessful', false),
    referenceNumber: get(detail, 'referenceNumber', ''),
  };
};

export const getMonetaryDetails = detail => {
  const exchangeRates = get(detail, 'exchangeRate', []);

  const details = [
    { title: <FormattedMessage id="transaction.transfer.totalDebit" />, info: get(detail, 'debitAmountDis', '') },
    { title: <FormattedMessage id="transaction.transfer.equivalentDebit" />, info: get(detail, 'localAmountDis', '') },
  ];

  if (Array.isArray(exchangeRates) && !isEmpty(exchangeRates)) {
    const exchangeRateDisplay = Children.toArray(
      exchangeRates.map(rate => (
        <>
          {rate}
          <br />
        </>
      ))
    );

    return details.concat({ title: <FormattedMessage id="transaction.transfer.exchangeRate" />, info: exchangeRateDisplay });
  }

  return details;
};

export const getReceiverDetail = (detail, transferType, isFavouriteTransfer) => {
  const amountHandler = {
    [E_WALLET]: get(detail, 'toAmountDisplay', ''),
    [INTERBANK]: get(detail, 'fromAmountDis', ''),
    default: get(detail, 'toAmountDis', ''),
  };
  const defaultAmountHandler = get(amountHandler, 'default', '');

  const transactionAmount = amountHandler[transferType] || defaultAmountHandler;
  const receiverName = transferType === E_WALLET ? get(detail, 'name', '') : get(detail, 'toAccountHolderName', '');
  const transactionType = isFavouriteTransfer && transferType === E_WALLET ? get(detail, 'payeeName', '') : get(detail, 'serviceName', '');

  return {
    transactionAmount,
    receiverName: receiverName || get(detail, 'accountHolderName', ''),
    receiverAccount: get(detail, 'toAccountNumber', ''),
    transactionType,
  };
};

export const getRecurringDetails = ({ confirmationDetail, transferFrequency }) => {
  const transferDate = get(confirmationDetail, 'effectiveDate', TODAY);
  const isMonthly = transferFrequency === TRANSFER_RECURRING_MONTHLY;

  // If weekly, get the day's name
  const day = moment(transferDate, CONFIRMATION_DETAIL_DATE_FORMAT).day();
  const debitDate = isMonthly ? (
    moment(transferDate, CONFIRMATION_DETAIL_DATE_FORMAT).format('DD')
  ) : (
    <FormattedMessage id={`calendar.longDays.${day}`} />
  );

  const debitLabel = isMonthly ? (
    <FormattedMessage id="transaction.transfer.debitDate" />
  ) : (
    <FormattedMessage id="transaction.transfer.debitDay" />
  );

  return [
    { title: debitLabel, info: debitDate },
    { title: <FormattedMessage id="transaction.transfer.startDate" />, info: get(confirmationDetail, 'effectiveDateDisplay', '') },
    { title: <FormattedMessage id="transaction.transfer.endDate" />, info: get(confirmationDetail, 'terminationDateDisplay', '') },
  ];
};

export const getLLDTitle = transferType => {
  const title = {
    [OWN_ACCOUNT]: 'transaction.transfer.ownAccount.title',
    [SWIFT_TRANSFER]: 'transaction.transfer.swift.modal.name',
    default: '',
  };

  return title[transferType] || title.default;
};

export const getAlertTransferName = (transferType, formatMessage) => {
  const label = {
    [SKN_TRANSFER]: formatMessage({ id: 'transaction.transfer.sknTransfer.name' }),
    [RTGS_TRANSFER]: formatMessage({ id: 'transaction.transfer.rtgs.type' }),
    [VIRTUAL_ACCOUNT]: formatMessage({ id: 'transaction.transfer.virtualAccount.name' }),
    [E_WALLET]: formatMessage({ id: 'transaction.transfer.eWallet.name' }),
    default: '',
  };
  return label[transferType] || label.default;
};

export const getBeneficiaryBankLabels = banks => (Array.isArray(banks) ? banks.map(bank => bank.label) : []);

// add searchableString property for string keywords that are searchable in fav accounts
export const getSearchableFavouriteAccounts = accounts =>
  accounts.map(account => {
    const accountNumber = get(account, 'accountNumber', '');
    const transferDesc = get(account, 'transferDesc', '');
    const beneficiary = get(account, 'beneficiary', '');
    const bankName = get(account, 'bankName', '');
    const searchableString = `${accountNumber} ${transferDesc} ${beneficiary} ${bankName}`;

    return { ...account, searchableString };
  });

export const getFavouriteAccountInfo = (transferType, isAddNewFavourite, detail) => {
  const receiverAccount = get(detail, 'toAccountNo', '');
  const transactionType = get(detail, 'serviceName', '');
  const receiverName = get(detail, 'accountHolderName', '');
  // for e-wallet new favourite, append payeeName to receiverName
  const payeeName = get(detail, 'payeeName', '');
  const isEWalletAddFavourite = isAddNewFavourite && transferType === E_WALLET;
  return { receiverAccount, transactionType, receiverName: isEWalletAddFavourite ? `${payeeName} ${receiverName}` : receiverName };
};

const getSwiftFormDetailFromFavourite = detail => ({
  accountNumber: get(detail, 'beneAcctNumber', ''),
  beneficiaryBankSwiftCode: get(detail, 'toForeignBankCode', ''),
  beneficiaryBankName: get(detail, 'toForeignBankName', ''),
  beneficiaryBankAddress1: get(detail, 'toForeignBankAddress1', ''),
  beneficiaryBankAddress2: get(detail, 'toForeignBankAddress2', ''),
  beneficiaryBankCountry: get(detail, 'toForeignBankCountry', ''),
  beneficiaryName: get(detail, 'beneficiaryName', ''),
  beneficiaryAddressStreet: get(detail, 'beneAddress1', ''),
  beneficiaryAddressOther: get(detail, 'beneAddress2', ''),
  beneficiaryAddressCity: get(detail, 'beneAddress3', ''),
  beneficiaryEmail: get(detail, 'beneficiaryEmail', ''),
});

export const getFavouriteFormValue = (detail, transferType, swiftBankDetail) => {
  const accountNumber = get(detail, 'toAccountNumber', '') || get(detail, 'toAccountNo', '');
  const currency = get(detail, 'currencyCode', '');
  const beneficiaryEmail = get(detail, 'toAccountEmailAddress', '');
  const beneficiaryBankLabel = get(detail, 'bankName', '') || get(detail, 'payeeName', '');
  const beneficiaryName = get(detail, 'toAccountHolderName', '');
  const bankCode = get(detail, 'bankCode', '');
  const payeeCode = get(detail, 'payeeCode', '');
  const favouriteTransaction = true; // for backend params

  const handler = {
    [INTRABANK]: () => ({ accountNumber, currency, favouriteTransaction }),
    [INTERBANK]: () => ({ accountNumber, beneficiaryEmail, beneficiaryBankLabel, favouriteTransaction }),
    [E_WALLET]: () => ({ accountNumber, beneficiaryBankLabel, favouriteTransaction, payeeCode }),
    [SKN_TRANSFER]: () => ({ accountNumber, beneficiaryBankLabel, beneficiaryName, beneficiaryEmail, favouriteTransaction, bankCode }),
    [RTGS_TRANSFER]: () => ({ accountNumber, beneficiaryBankLabel, beneficiaryName, beneficiaryEmail, favouriteTransaction, bankCode }),
    [VIRTUAL_ACCOUNT]: () => ({ accountNumber, beneficiaryEmail, favouriteTransaction }),
    [SWIFT_TRANSFER]: () => getSwiftFormDetailFromFavourite(swiftBankDetail),
    default: () => {},
  };

  return (handler[transferType] || handler.default)();
};
